import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import type { Group, Mesh } from 'three';
import type { BinColor } from '../../data/schemas';
import { BIN_HEX } from './colors';
import { clawRotationZ, sideArmRotationZ, tipDumpBodyTransform } from './finishManeuver';
import {
  FALLING_PIECES,
  LID_HINGE,
  PILE_SLOTS,
  fallingPiece,
  lidAngle,
  pileCount,
  trashPiece,
} from './dumpAnimation';
import { TrashPiece } from './BinMesh';

/** Road surface height (road slab top ≈ 0.085): wheels rest on it instead of sinking in. */
export const TRUCK_GROUND_Y = 0.09;

export interface TruckModelProps {
  pose: { x: number; z: number; yaw: number };
  signColor?: BinColor | null;
  /** 0..1 lift progress for side arm dump (~1.2s). */
  armLift?: number;
  dumping?: boolean;
  /** How full the hopper looks (0..1) from emptied bins. */
  loadAmount?: number;
  /** 0..1 openness of the hopper lid during a bin dump. */
  lidOpen?: number;
  /** 0..1 progress of the current bin dump (drives the falling rubbish); 0 when idle. */
  dumpT?: number;
  /** Colour of the bin being emptied (rubbish palette). */
  dumpColor?: BinColor | null;
  /** Start-phase pulse so the child taps the truck. */
  pulse?: boolean;
  /** Blink headlights (start / finish dump cue). */
  headlightsBlink?: boolean;
  /** Tip the whole load at the plant. */
  tipDump?: boolean;
  onTap?: () => void;
  /** Invisible hit target active when pulse/finish tap is needed. */
  tapEnabled?: boolean;
}

/** Kid-friendly rear-loader garbage truck — sloped back, loading mouth, side arm. */
export const TruckModel: React.FC<TruckModelProps> = ({
  pose,
  signColor = null,
  armLift = 0,
  dumping = false,
  loadAmount = 0,
  lidOpen = 0,
  dumpT = 0,
  dumpColor = null,
  pulse = false,
  headlightsBlink = false,
  tipDump = false,
  onTap,
  tapEnabled = false,
}) => {
  const group = useRef<Group>(null);
  const wheelSpins = useRef<Group[]>([]);
  const beacon = useRef<Mesh>(null);
  const hopperGroup = useRef<Group>(null);
  const pileGroup = useRef<Group>(null);
  const tipCur = useRef(0);
  const slideCur = useRef(0);
  const hlMats = useRef<Mesh[]>([]);
  const lidGroup = useRef<Group>(null);
  const lidCur = useRef(0);
  const bounce = useRef(0);
  const prevPile = useRef(pileCount(loadAmount));

  useFrame((state, dt) => {
    if (!group.current) return;
    group.current.position.set(pose.x, TRUCK_GROUND_Y, pose.z);
    group.current.rotation.y = -pose.yaw;

    if (pulse) {
      const s = 1 + Math.sin(state.clock.elapsedTime * 2.4) * 0.04;
      group.current.scale.set(s, s, s);
    } else {
      group.current.scale.set(1, 1, 1);
    }

    if (hopperGroup.current) {
      // Tip progress 0..1: ~0.6 s up, ~0.5 s back down
      const rate = (tipDump ? 1.7 : 2.0) * dt;
      const target = tipDump ? 1 : 0;
      tipCur.current += Math.max(-rate, Math.min(rate, target - tipCur.current));
      const eased = tipCur.current * tipCur.current * (3 - 2 * tipCur.current);
      // Hopper rotates about its rear hinge: rear stays on the floor, front lifts
      const tip = tipDumpBodyTransform(eased);
      hopperGroup.current.rotation.z = tip.rotZ;
      hopperGroup.current.position.x = tip.x;
      // Small bounce when new rubbish lands in the hopper
      bounce.current = Math.max(0, bounce.current - dt * 3.5);
      hopperGroup.current.position.y = tip.y + Math.sin(bounce.current * Math.PI) * 0.09;

      // Once tipped, the pile slides out of the rear and drops into the pit
      if (tipDump && tipCur.current > 0.85) {
        slideCur.current = Math.min(1, slideCur.current + dt / 0.9);
      } else if (!tipDump && tipCur.current <= 0.01) {
        slideCur.current = 0;
      }
      if (pileGroup.current) {
        const sl = slideCur.current;
        const e = sl * sl;
        pileGroup.current.position.set(-e * 2.8, -e * 1.6, 0);
        const shrink = sl > 0.75 ? Math.max(0.001, 1 - (sl - 0.75) / 0.25) : 1;
        pileGroup.current.scale.setScalar(shrink);
      }
    }

    if (lidGroup.current) {
      const target = tipDump ? 0.8 : lidOpen;
      lidCur.current += (target - lidCur.current) * Math.min(1, dt * 9);
      lidGroup.current.rotation.z = -lidAngle(loadAmount, lidCur.current);
    }

    if (!dumping && !tipDump) {
      wheelSpins.current.forEach((w) => {
        if (w) w.rotation.z += dt * 6;
      });
    }
    if (beacon.current) {
      const pulseB = 0.55 + Math.sin(state.clock.elapsedTime * 4) * 0.45;
      const mat = beacon.current.material as { emissiveIntensity?: number };
      if (mat.emissiveIntensity != null) mat.emissiveIntensity = pulseB;
    }
    hlMats.current.forEach((m) => {
      if (!m) return;
      const mat = m.material as { emissiveIntensity?: number };
      if (mat.emissiveIntensity == null) return;
      if (headlightsBlink) {
        mat.emissiveIntensity = 0.3 + (Math.sin(state.clock.elapsedTime * 10) > 0 ? 1.2 : 0.2);
      } else {
        mat.emissiveIntensity = 0.6;
      }
    });
  });

  const handleTap = (e: { stopPropagation: () => void }) => {
    // Only swallow the event when the truck is actually tappable; otherwise
    // let it reach bins/litter that sit behind the truck from the camera's view.
    if (!tapEnabled) return;
    e.stopPropagation();
    onTap?.();
  };

  // Arm swing: reach out → lift up → tip → return (driven by armLift 0..1)
  const armReach = Math.sin(Math.min(1, armLift * 1.2) * Math.PI) * 0.9;
  const armRaise = armLift > 0.25 ? Math.min(1, (armLift - 0.25) / 0.5) * 1.1 : 0;
  const armTip = armLift > 0.55 ? Math.min(1, (armLift - 0.55) / 0.35) * 0.8 : 0;

  const pieces = pileCount(loadAmount);
  if (pieces > prevPile.current) bounce.current = 1;
  prevPile.current = pieces;

  return (
    <group ref={group} name="vehicle-soppelbilen">
      {/* Generous tap target (≥96px). Mounted only while the truck is tappable so it
          never sits in front of bins. Note: visible={false} does NOT disable raycasting. */}
      {tapEnabled ? (
        <mesh name="truck-hit" position={[0, 1.2, 0]} onPointerDown={handleTap}>
          <boxGeometry args={[4.8, 3.6, 3.2]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
      ) : null}

      {pulse ? (
        <mesh name="truck-pulse-ring" position={[0, 0.06, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[1.6, 2.15, 32]} />
          <meshBasicMaterial color="#FFFFFF" transparent opacity={0.55} depthWrite={false} />
        </mesh>
      ) : null}

      {/* Chassis */}
      <mesh name="truck-chassis" position={[0, 0.22, 0]} castShadow>
        <boxGeometry args={[3.6, 0.28, 1.7]} />
        <meshStandardMaterial color="#4B5563" roughness={0.75} />
      </mesh>

      {/* Cab + front: stays level */}
      <group name="truck-cab-group">
        {/* Cab */}
        <mesh name="truck-cab" position={[1.15, 1.15, 0]} castShadow onPointerDown={handleTap}>
          <boxGeometry args={[1.35, 1.35, 1.75]} />
          <meshStandardMaterial color="#FFC94A" roughness={0.45} metalness={0.05} />
        </mesh>
        <mesh name="truck-cab-roof" position={[1.15, 1.9, 0]} castShadow>
          <boxGeometry args={[1.25, 0.28, 1.65]} />
          <meshStandardMaterial color="#FFB82E" roughness={0.5} />
        </mesh>

        {/* Windshield + face */}
        <mesh name="truck-windshield" position={[1.82, 1.35, 0]} castShadow>
          <boxGeometry args={[0.12, 0.75, 1.45]} />
          <meshStandardMaterial color="#9FE7FF" roughness={0.15} metalness={0.2} transparent opacity={0.92} />
        </mesh>
        <mesh name="truck-eye-l" position={[1.9, 1.45, 0.35]}>
          <sphereGeometry args={[0.12, 10, 10]} />
          <meshStandardMaterial color="#1F2937" />
        </mesh>
        <mesh name="truck-eye-r" position={[1.9, 1.45, -0.35]}>
          <sphereGeometry args={[0.12, 10, 10]} />
          <meshStandardMaterial color="#1F2937" />
        </mesh>
        <mesh name="truck-eye-shine-l" position={[1.94, 1.5, 0.38]}>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial color="#FFFFFF" />
        </mesh>
        <mesh name="truck-eye-shine-r" position={[1.94, 1.5, -0.32]}>
          <sphereGeometry args={[0.04, 8, 8]} />
          <meshStandardMaterial color="#FFFFFF" />
        </mesh>
        <mesh name="truck-side-window" position={[1.15, 1.35, 0.9]}>
          <boxGeometry args={[0.7, 0.55, 0.06]} />
          <meshStandardMaterial color="#9FE7FF" roughness={0.2} transparent opacity={0.85} />
        </mesh>
        <mesh name="truck-side-eye-l" position={[1.3, 1.42, 0.94]}>
          <sphereGeometry args={[0.09, 10, 10]} />
          <meshStandardMaterial color="#1F2937" />
        </mesh>
        <mesh name="truck-side-eye-r" position={[1.0, 1.42, 0.94]}>
          <sphereGeometry args={[0.09, 10, 10]} />
          <meshStandardMaterial color="#1F2937" />
        </mesh>
        <mesh name="truck-side-smile" position={[1.15, 1.2, 0.94]}>
          <boxGeometry args={[0.28, 0.05, 0.04]} />
          <meshStandardMaterial color="#1F2937" />
        </mesh>

        <mesh name="truck-bumper" position={[1.95, 0.45, 0]} castShadow>
          <boxGeometry args={[0.25, 0.28, 1.85]} />
          <meshStandardMaterial color="#F97316" roughness={0.55} />
        </mesh>

        {(
          [
            [2.0, 0.7, 0.55],
            [2.0, 0.7, -0.55],
          ] as [number, number, number][]
        ).map((p, i) => (
          <mesh
            key={`hl-${i}`}
            name={`truck-headlight-${i}`}
            position={p}
            ref={(el) => {
              if (el) hlMats.current[i] = el;
            }}
          >
            <sphereGeometry args={[0.14, 10, 10]} />
            <meshStandardMaterial color="#FFF7CC" emissive="#FFE66D" emissiveIntensity={0.6} />
          </mesh>
        ))}

        <mesh ref={beacon} name="truck-beacon" position={[1.15, 2.2, 0]}>
          <sphereGeometry args={[0.16, 12, 12]} />
          <meshStandardMaterial color="#FF6B6B" emissive="#FF6B6B" emissiveIntensity={0.7} />
        </mesh>

        {signColor ? (
          <group name="truck-sign" position={[1.65, 2.05, 0]}>
            <mesh castShadow>
              <boxGeometry args={[0.12, 0.95, 0.95]} />
              <meshStandardMaterial
                color={BIN_HEX[signColor]}
                emissive={BIN_HEX[signColor]}
                emissiveIntensity={0.35}
              />
            </mesh>
          </group>
        ) : null}

        {/* Hopper (cargo body): the only part that tips at the plant, about its rear hinge */}
        <group ref={hopperGroup} name="truck-hopper">
        {/* Rear-loader hopper — sloped back + loading mouth */}
        <mesh name="truck-body" position={[-0.7, 1.2, 0]} castShadow onPointerDown={handleTap}>
          <boxGeometry args={[2.1, 1.45, 1.9]} />
          <meshStandardMaterial color="#3DB8F5" roughness={0.4} metalness={0.08} />
        </mesh>
        {/* Sloped rear panel */}
        <mesh
          name="truck-rear-slope"
          position={[-1.85, 1.15, 0]}
          rotation={[0, 0, 0.45]}
          castShadow
        >
          <boxGeometry args={[0.9, 1.5, 1.85]} />
          <meshStandardMaterial color="#2EA8E6" roughness={0.42} />
        </mesh>
        {/* Loading mouth opening at rear */}
        <mesh name="truck-mouth" position={[-2.15, 0.85, 0]} castShadow>
          <boxGeometry args={[0.35, 0.85, 1.55]} />
          <meshStandardMaterial color="#1E3A5F" roughness={0.7} />
        </mesh>
        <mesh name="truck-mouth-lip" position={[-2.0, 1.35, 0]}>
          <boxGeometry args={[0.2, 0.12, 1.6]} />
          <meshStandardMaterial color="#FFC94A" />
        </mesh>

        {/* Rubbish pile in the hopper: grows with every emptied bin */}
        <group ref={pileGroup} name="truck-load">
          {PILE_SLOTS.slice(0, pieces).map((pos, i) => (
            <TrashPiece
              key={i}
              def={trashPiece((['green', 'blue', 'red'] as BinColor[])[i % 3], Math.floor(i / 3))}
              position={pos}
              rotation={[0.4 * i, 0.8 * i, 0.3 * i]}
            />
          ))}
        </group>

        {/* Hopper lid, hinged just behind the cab: opens up for a dump, rests ajar as load grows */}
        <group ref={lidGroup} name="truck-hopper-lid" position={[LID_HINGE.x, LID_HINGE.y, 0]}>
          <mesh position={[-0.95, 0.05, 0]} castShadow>
            <boxGeometry args={[1.9, 0.12, 1.65]} />
            <meshStandardMaterial color="#2EA8E6" roughness={0.45} />
          </mesh>
        </group>

        <mesh name="truck-stripe" position={[-0.7, 1.0, 0.96]}>
          <boxGeometry args={[1.8, 0.22, 0.04]} />
          <meshStandardMaterial color="#FFE66D" />
        </mesh>
        <mesh name="truck-stripe-b" position={[-0.7, 1.0, -0.96]}>
          <boxGeometry args={[1.8, 0.22, 0.04]} />
          <meshStandardMaterial color="#FFE66D" />
        </mesh>

        {/* Side lifting arm on curb side (+Z) — raises into the hopper on pickup */}
        <group
          name="truck-lift-arm"
          position={[-0.15, 1.35, 1.05]}
          rotation={[0, 0, sideArmRotationZ(armRaise, armTip)]}
        >
          <mesh name="truck-arm" position={[-0.55 - armReach * 0.4, 0, 0]} castShadow>
            <boxGeometry args={[1.4, 0.16, 0.16]} />
            <meshStandardMaterial color="#6B7280" metalness={0.3} roughness={0.45} />
          </mesh>
          <mesh
            name="truck-claw"
            position={[-1.25 - armReach * 0.4, -0.15 + armRaise * 0.3, 0]}
            rotation={[0, 0, clawRotationZ(armTip)]}
            castShadow
          >
            <boxGeometry args={[0.36, 0.45, 0.55]} />
            <meshStandardMaterial color="#9CA3AF" metalness={0.25} roughness={0.5} />
          </mesh>
        </group>
        </group>
      </group>

      {/* Rubbish falling out of the tipped bin into the open hopper */}
      {dumpColor
        ? Array.from({ length: FALLING_PIECES }, (_, i) => {
            const fp = fallingPiece(i, dumpT);
            if (!fp) return null;
            return (
              <TrashPiece
                key={`fall-${i}`}
                def={trashPiece(dumpColor, i)}
                position={[fp.x, fp.y, fp.z]}
                rotation={[fp.spin, fp.spin * 0.7, 0]}
              />
            );
          })
        : null}

      {/* Wheels */}
      {(
        [
          [1.1, 0.38, 0.98],
          [1.1, 0.38, -0.98],
          [-1.3, 0.38, 0.98],
          [-1.3, 0.38, -0.98],
        ] as [number, number, number][]
      ).map((p, i) => (
        <group
          key={i}
          position={p}
          ref={(el) => {
            if (el) wheelSpins.current[i] = el;
          }}
        >
          <mesh name={`truck-wheel-${i}`} rotation={[Math.PI / 2, 0, 0]} castShadow>
            <cylinderGeometry args={[0.38, 0.38, 0.32, 14]} />
            <meshStandardMaterial color="#1F2937" roughness={0.85} />
          </mesh>
          <mesh name={`truck-hub-${i}`} rotation={[Math.PI / 2, 0, 0]}>
            <cylinderGeometry args={[0.16, 0.16, 0.34, 10]} />
            <meshStandardMaterial color="#FFC94A" metalness={0.2} roughness={0.4} />
          </mesh>
        </group>
      ))}
    </group>
  );
};

export default TruckModel;
